import React, { useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  Text,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  StatusBar,
  StyleSheet,
} from 'react-native';

import { useRoute, RouteProp } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThreadDetail } from '@/hooks/residents/corkBoard/useThreadDetail';
import { ThreadHeader } from '@/components/residents/corkBoard/ThreadHeader';
import { ThreadBody } from '@/components/residents/corkBoard/ThreadBody';
import { ReplyCard } from '@/components/residents/corkBoard/ReplyCard';
import { ReplyInput } from '@/components/residents/corkBoard/ReplyInput';
import { ThreadActionsMenu } from '@/components/residents/corkBoard/ThreadActionsMenu';
import { CorkBoardReportModal } from '@/components/residents/corkBoard/CorkBoardReportModal';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { useAttachmentViewer } from '@/hooks/residents/corkBoard/useAttachmentViewer';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { CorkBoardReply } from '@/types/residents/corkBoard';

type ThreadDetailRouteProp = RouteProp<RootStackParamList, 'ThreadDetail'>;

export const ThreadDetailScreen: React.FC = () => {
  const route = useRoute<ThreadDetailRouteProp>();
  const { threadId } = route.params;
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();

  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';

  const {
    thread,
    accumulatedReplies,
    hasMoreReplies,
    isInitialLoad,
    isFetching,
    isError,
    isSubmittingReply,
    isReporting,

    isActionsMenuVisible,
    isReportModalVisible,
    isDeleteConfirmVisible,
    isDeleteReplyConfirmVisible,
    hasReported,
    reportContentType,

    refetch,
    handleGoBack,
    handleLoadMoreReplies,
    handleSubmitReply,
    handleToggleSave,
    handleDeleteThread,
    handleDeleteReply,
    handleReportSubmit,
    handleEditThread,

    openActionsMenu,
    closeActionsMenu,
    openReportModal,
    closeReportModal,
    openDeleteConfirm,
    closeDeleteConfirm,
    openDeleteReplyConfirm,
    closeDeleteReplyConfirm,
  } = useThreadDetail(threadId);

  const {
    viewerVisible,
    viewerImages,
    viewerIndex,
    openImageViewer,
    closeImageViewer,
    openDocument,
  } = useAttachmentViewer();

  // ── Reply list callbacks ──────────────────────────────────────────────────
  const renderReply = useCallback(({ item }: { item: CorkBoardReply }) => (
    <View style={styles.replyWrapper}>
      <ReplyCard
        reply={item}
        isOP={thread ? item.creator.id === thread.creator.id : false}
        onDelete={item.isOwnReply ? () => openDeleteReplyConfirm(item.id) : undefined}
        onReport={!item.isOwnReply ? () => openReportModal({ replyId: Number(item.id) }) : undefined}
        onImagePress={openImageViewer}
        onDocumentPress={openDocument}
      />
    </View>
  ), [thread, openReportModal, openDeleteReplyConfirm, openImageViewer, openDocument]);

  const keyExtractor = useCallback((item: CorkBoardReply) => item.id, []);

  const ReplyCellRenderer = useCallback((props: any) => (
    <View {...props} style={[props.style, { zIndex: -props.index }]} />
  ), []);

  // ── List header: thread content ───────────────────────────────────────────
  const ListHeaderComponent = useMemo(() => {
    if (!thread || !thread.id) return null;
    return (
      <View style={styles.threadSection}>
        <ThreadHeader thread={thread} />

        <View style={styles.divider} />

        <ThreadBody
          body={thread.body}
          attachments={thread.attachments}
          onImagePress={openImageViewer}
          onDocumentPress={openDocument}
        />

        {/* Action buttons */}
        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleToggleSave}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={thread.isSaved ? 'Unsave thread' : 'Save thread'}
          >
            <Text style={styles.actionText}>{thread.isSaved ? 'Saved' : 'Save'}</Text>
          </TouchableOpacity>

          {!thread.isOwnThread && (
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => openReportModal()}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Report this thread"
            >
              <Text style={styles.actionText}>Report</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Replies divider */}
        <View style={styles.repliesDivider}>
          <Text style={styles.repliesLabel}>
            Replies ({thread.replyCount})
          </Text>
        </View>
      </View>
    );
  }, [thread, handleToggleSave, openReportModal, openImageViewer, openDocument]);

  // ── List footer: loading more indicator ───────────────────────────────────
  const ListFooterComponent = useMemo(() => {
    if (!isFetching || !hasMoreReplies) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={SEMANTIC_COLORS.interactive.primary} />
      </View>
    );
  }, [isFetching, hasMoreReplies]);

  const ListEmptyComponent = useMemo(() => {
    if (isFetching) return null;
    return (
      <View style={styles.emptyReplies}>
        <Text style={styles.emptyRepliesText}>No replies yet. Be the first to respond!</Text>
      </View>
    );
  }, [isFetching]);

  // ── Loading / Error states ────────────────────────────────────────────────
  if (isInitialLoad) {
    return (
      <View style={[styles.screenContainer, { paddingTop: isMobile ? insets.top : 0 }]}>
        {!isDesktop && <StatusBar barStyle="light-content" />}
        <ScreenHeader onBack={handleGoBack} />
        <View style={styles.centeredContainer}>
          <ActivityIndicator size="large" color={SEMANTIC_COLORS.interactive.primary} />
        </View>
      </View>
    );
  }

  if (isError && !thread) {
    return (
      <View style={[styles.screenContainer, { paddingTop: isMobile ? insets.top : 0 }]}>
        {!isDesktop && <StatusBar barStyle="light-content" />}
        <ScreenHeader onBack={handleGoBack} />
        <View style={styles.centeredContainer} accessibilityRole="alert">
          <Text style={styles.errorText}>Failed to load thread</Text>
          <TouchableOpacity style={styles.retryButton} onPress={refetch} accessibilityRole="button" accessibilityLabel="Retry loading thread">
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.screenContainer, { paddingTop: isMobile || isTablet ? insets.top : 0 }]}>
      {!isDesktop && <StatusBar barStyle="light-content" />}

      <KeyboardAvoidingView
        style={[
          styles.keyboardAvoidingView,
          !(isMobile || isTablet) && styles.desktopWrapper,
        ]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        {/* Header bar */}
        <ScreenHeader
          onBack={handleGoBack}
          onMore={thread ? openActionsMenu : undefined}
          actionsMenu={thread ? (
            <ThreadActionsMenu
              visible={isActionsMenuVisible}
              onClose={closeActionsMenu}
              isOwnThread={thread.isOwnThread}
              onEdit={handleEditThread}
              onDelete={openDeleteConfirm}
              onReport={() => openReportModal()}
              onSave={handleToggleSave}
              isSaved={thread.isSaved}
            />
          ) : undefined}
        />

        {/* Replies list with thread content as header */}
        <FlatList
          data={accumulatedReplies}
          renderItem={renderReply}
          keyExtractor={keyExtractor}
          CellRendererComponent={ReplyCellRenderer}
          ListHeaderComponent={ListHeaderComponent}
          ListEmptyComponent={ListEmptyComponent}
          ListFooterComponent={ListFooterComponent}
          contentContainerStyle={styles.listContent}
          ItemSeparatorComponent={ReplySeparator}
          onEndReached={handleLoadMoreReplies}
          onEndReachedThreshold={0.4}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews={Platform.OS !== 'web'}
          maxToRenderPerBatch={10}
          windowSize={5}
          initialNumToRender={15}
          updateCellsBatchingPeriod={50}
        />

        {/* Reply input */}
        {thread && (
          <View style={{ paddingBottom: isMobile ? insets.bottom : 0, backgroundColor: SEMANTIC_COLORS.background.secondary }}>
            <ReplyInput
              isLocked={thread.isLocked}
              onSubmit={handleSubmitReply}
              isSubmitting={isSubmittingReply}
            />
          </View>
        )}
      </KeyboardAvoidingView>

      {/* Modals */}
      {thread && (
        <>
          <CorkBoardReportModal
            visible={isReportModalVisible}
            onClose={closeReportModal}
            onSubmit={handleReportSubmit}
            isSubmitting={isReporting}
            hasAlreadyReported={hasReported}
            contentType={reportContentType}
          />

          <ConfirmationModal
            visible={isDeleteConfirmVisible}
            message="Are you sure you want to delete this thread? This action cannot be undone."
            onCancel={closeDeleteConfirm}
            onProceed={handleDeleteThread}
            cancelText="Cancel"
            proceedText="Delete"
          />

          <ConfirmationModal
            visible={isDeleteReplyConfirmVisible}
            message="Are you sure you want to delete this reply? This action cannot be undone."
            onCancel={closeDeleteReplyConfirm}
            onProceed={handleDeleteReply}
            cancelText="Cancel"
            proceedText="Delete"
          />
        </>
      )}

      <ImageViewerModal
        visible={viewerVisible}
        images={viewerImages}
        initialIndex={viewerIndex}
        onClose={closeImageViewer}
      />
    </View>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface ScreenHeaderProps {
  onBack: () => void;
  onMore?: () => void;
  actionsMenu?: React.ReactNode;
}

const ScreenHeader: React.FC<ScreenHeaderProps> = ({ onBack, onMore, actionsMenu }) => (
  <View style={styles.header}>
    <TouchableOpacity
      style={styles.headerButton}
      onPress={onBack}
      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      accessibilityRole="button"
      accessibilityLabel="Go back"
    >
      <RESIDENTS_SVG_COMPONENTS.CHEVRON_LEFT width={20} height={20} color={SEMANTIC_COLORS.text.primary} />
      <Text style={styles.headerBackText}>Back</Text>
    </TouchableOpacity>
    {onMore && (
      <View style={styles.menuAnchor}>
        <TouchableOpacity
          style={styles.headerButton}
          onPress={onMore}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Thread actions"
        >
          <RESIDENTS_SVG_COMPONENTS.THREE_DOTS width={20} height={20} color={SEMANTIC_COLORS.text.primary} />
        </TouchableOpacity>
        {actionsMenu}
      </View>
    )}
  </View>
);

const ReplySeparator = () => <View style={styles.replySeparator} />;

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: SEMANTIC_COLORS.background.primary,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  desktopWrapper: {
    maxWidth: 700,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray800,
    overflow: 'visible',
    zIndex: 10,
  },
  headerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  menuAnchor: {
    position: 'relative',
  },
  headerBackText: {
    fontSize: 16,
    color: SEMANTIC_COLORS.text.primary,
    fontWeight: '500',
  },
  listContent: {
    paddingBottom: 16,
  },
  threadSection: {
    padding: 16,
    gap: 14,
  },
  divider: {
    height: 1,
    backgroundColor: PRIMITIVE_COLORS.gray800,
    marginVertical: 2,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingTop: 4,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.secondary,
  },
  repliesDivider: {
    paddingTop: 8,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: PRIMITIVE_COLORS.gray800,
    marginTop: 4,
  },
  repliesLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  replyWrapper: {
    paddingHorizontal: 16,
  },
  replySeparator: {
    height: 8,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyReplies: {
    paddingVertical: 40,
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  emptyRepliesText: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.muted,
    textAlign: 'center',
  },
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    fontSize: 16,
    color: SEMANTIC_COLORS.text.secondary,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: SEMANTIC_COLORS.interactive.primary,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
